import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import LoginView from "@/components/LoginView.vue";
import store from "@/store";
import BaseLayout from "@/layouts/BaseLayout.vue";
import AdminLayout from "@/layouts/AdminLayout.vue";
import ClientContextLayout from "@/layouts/ClientContextLayout.vue";
// import { Auth } from "aws-amplify";

const routes = [
  {
    path: "/",
    component: BaseLayout,
    children: [
      {
        path: "",
        name: "home",
        component: HomeView,
        meta: { title: "Home" },
        beforeEnter: async (to, from, next) => {
          if (to.query.code) {
            next({ name: "auth-handler", query: { code: to.query.code } });
            return;
          }
          next();
        },
      },
      {
        path: "about",
        name: "about",
        component: () => import("../views/AboutView.vue"),
        meta: { title: "About Us" },
      },
      {
        path: "services",
        name: "services",
        component: () => import("../views/ServicesView.vue"),
        meta: { title: "Our Services" },
      },
      {
        path: "contact",
        name: "contact",
        component: () => import("../views/ContactView.vue"),
        meta: { title: "Contact Us" },
      },
      {
        path: "blogs",
        name: "blogs",
        component: () => import("../views/BlogView.vue"),
        meta: { title: "Blogs" },
      },
      {
        path: "blog/:id",
        name: "blog-post",
        component: () => import("../views/BlogPostView.vue"),
        meta: { title: "Blog Post" },
      },
      {
        path: "tools/retirement-calculator",
        name: "retirement-calculator",
        component: () => import("../views/tools/RetirementCalculator.vue"),
        meta: { title: "Retirement Calculator" },
      },
      {
        path: "tools/cagr-calculator",
        name: "cagr-calculator",
        component: () => import("../views/tools/CagrCalculator.vue"),
        meta: { title: "CAGR Calculator" },
      },
      {
        path: "tools/health-report",
        name: "health-report",
        component: () => import("../views/tools/HealthReport.vue"),
        meta: {
          title: "Financial Health Report",
          breadcrumb: [
            { text: "Tools", to: "/tools" },
            { text: "Health Report", to: "/tools/health-report" },
          ],
        },
      },
      {
        path: "register",
        name: "student-registration",
        redirect: "/",
        meta: {
          title: "Student Registration",
          public: true,
        },
      },
      // {
      //   path: "register",
      //   name: "student-registration",
      //   component: () =>
      //     import("../views/registration/StudentRegistration.vue"),
      //   meta: {
      //     title: "Student Registration",
      //     public: true,
      //   },
      // },
      {
        path: "dashboard",
        name: "dashboard",
        component: () => import("../views/DashboardView.vue"),
        meta: { requiresAuth: true, title: "Dashboard" },
      },
    ],
  },
  {
    path: "/auth",
    name: "auth-handler",
    component: () => import("../components/AuthHandler.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
  },
  {
    path: "/admin",
    component: AdminLayout,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "",
        name: "admin-dashboard",
        component: () => import("../views/AdminDashboard.vue"),
        meta: { title: "Admin Dashboard" },
      },
      {
        path: "provisioning",
        name: "client-provisioning",
        component: () => import("../views/ClientProvisioningView.vue"),
        meta: { title: "Client Provisioning" },
      },
      {
        path: "clients",
        name: "client-list",
        component: () => import("../views/ClientListView.vue"),
        meta: { title: "Client List" },
      },
      {
        path: "investments",
        name: "admin-investments",
        component: () => import("../views/AdminInvestmentsView.vue"),
        meta: { title: "Investments" },
      },
      {
        path: "reports",
        name: "admin-reports",
        component: () => import("../views/AdminReportsView.vue"),
        meta: { title: "Reports" },
      },
      {
        path: "settings",
        name: "admin-settings",
        component: () => import("../views/AdminSettingsView.vue"),
        meta: { title: "Settings" },
      },
    ],
  },
  {
    path: "/client/:id",
    component: ClientContextLayout,
    meta: { requiresAuth: true, requiresAdmin: true, title: "Client Details" },
    children: [
      {
        path: "",
        name: "client-details",
        component: () =>
          import("../components/admin/client-views/ClientOverview.vue"),
        props: true,
        meta: { title: "Client Overview" },
      },
      {
        path: "overview",
        name: "client-overview",
        component: () =>
          import("../components/admin/client-views/ClientOverview.vue"),
        props: true,
        meta: { title: "Client Overview" },
      },
      {
        path: "goals-summary",
        name: "client-goals-summary",
        component: () =>
          import("../components/admin/client-views/ClientGoalsSummary.vue"),
        props: true,
        meta: { title: "Goals Summary" },
      },
      {
        path: "planning",
        name: "client-planning",
        component: () =>
          import("../components/admin/client-views/ClientPlanning.vue"),
        props: true,
        meta: { title: "Planning Review" },
      },
      {
        path: "sip",
        name: "client-sip",
        component: () =>
          import("../components/admin/client-views/ClientSIP.vue"),
        props: true,
        meta: { title: "SIP Management" },
      },
      {
        path: "goals",
        name: "client-goals",
        component: () =>
          import("../components/admin/client-views/ClientGoals.vue"),
        props: true,
        meta: { title: "Financial Goals" },
      },
      {
        path: "documents",
        name: "client-documents",
        component: () =>
          import("../components/admin/client-views/ClientDocuments.vue"),
        props: true,
        meta: { title: "Documents" },
      },
      {
        path: "transactions",
        name: "client-transactions",
        component: () =>
          import("../components/admin/client-views/ClientTransactions.vue"),
        props: true,
        meta: { title: "Transactions" },
      },
      {
        path: "assets",
        name: "client-assets",
        component: () =>
          import("../components/admin/client-views/ClientAssets.vue"),
        props: true,
        meta: { title: "Client Assets" },
      },
      {
        path: "allocations",
        name: "client-allocations",
        component: () =>
          import("../components/admin/client-views/AssetAllocationView.vue"),
        props: true,
        meta: { title: "Asset Allocations" },
      },
    ],
  },
  {
    path: "/admin/temp-redirect",
    name: "temp-redirect",
    component: {
      render: () => null,
    },
    meta: { requiresAuth: true, requiresAdmin: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Add navigation guard for protected routes
router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated) {
      console.log("redirecting to login");
      next({
        path: "/login",
        query: { redirect: to.fullPath },
      });
    } else if (to.matched.some((record) => record.meta.requiresAdmin)) {
      // Check if user has admin role
      const user = store.getters.getUser;
      if (!user?.isAdmin) {
        console.log("redirecting to home");
        next({ path: "/" });
      } else {
        console.log("redirecting to dashboard");
        next();
      }
    } else {
      console.log("redirecting to dashboard");
      next();
    }
  } else {
    next();
  }
});

export default router;
